import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  runTransaction,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  COLLECTIONS,
  VALIDATION,
  LOG_TYPES,
  APPOINTMENT_TYPES,
  REMINDER_MINUTES_OPTIONS,
} from '../config/constants';
import { AppError, FirestoreError } from '../lib/errors';

/**
 * Servicio de citas del Calendario.
 *
 * Una cita (appointment) representa un compromiso futuro con una o más
 * mascotas: visita al vet, peluquería, vacuna, antiparasitario, paseo, etc.
 *
 * Decisiones de modelado:
 *  - Multi-pet (`petIds[]`): una cita puede afectar varias mascotas
 *    del mismo hogar (p.ej. vacunatorio grupal). Snapshot al momento de
 *    crear — si las pets cambian de nombre o se soft-deletean después,
 *    el snapshot mantiene la referencia histórica.
 *  - `petIds` inmutable post-creación (rules + service): si el usuario
 *    quiere otras pets, crea una cita nueva. Más simple, evita confusión
 *    histórica.
 *  - Cross-tenant defense de `petIds[]` vive en el SERVICE, no en rules.
 *    Las Firestore Rules limitan los `get()` y validar N pets en una rule
 *    sería frágil. El service lee cada pet referenciado y verifica que
 *    `pet.familyId === appointment.familyId`.
 *  - Estados mutuamente excluyentes (completedAt vs cancelledAt vs deletedAt):
 *    validados en service, no en rule (la rule solo bloquea cambios de
 *    familyId/createdBy/petIds y tipos inválidos).
 */

const ALLOWED_APPOINTMENT_TYPES = Object.values(APPOINTMENT_TYPES);

// =====================================================================
// VALIDADORES
// =====================================================================

const validateTitle = (title) => {
  if (!title || typeof title !== 'string' || !title.trim()) {
    throw new AppError('El título de la cita es requerido', 'INVALID_TITLE');
  }
  if (title.length > VALIDATION.APPOINTMENT_TITLE_MAX) {
    throw new AppError(
      `El título supera los ${VALIDATION.APPOINTMENT_TITLE_MAX} caracteres`,
      'INVALID_TITLE'
    );
  }
};

const validateNotes = (notes) => {
  if (notes == null) return;
  if (typeof notes !== 'string') {
    throw new AppError('Las notas deben ser texto', 'INVALID_NOTES');
  }
  if (notes.length > VALIDATION.APPOINTMENT_NOTES_MAX) {
    throw new AppError(
      `Las notas superan los ${VALIDATION.APPOINTMENT_NOTES_MAX} caracteres`,
      'INVALID_NOTES'
    );
  }
};

const validateType = (type) => {
  if (!ALLOWED_APPOINTMENT_TYPES.includes(type)) {
    throw new AppError(
      `Tipo de cita inválido. Opciones: ${ALLOWED_APPOINTMENT_TYPES.join(', ')}`,
      'INVALID_TYPE'
    );
  }
};

/**
 * scheduledAt: acepta Date o Timestamp. Convierte a Date para validar.
 * NO rechaza fechas pasadas en update (caso: usuario marca como completada
 * una cita que pasó). En create, sí rechaza pasado (sin sentido agendar atrás).
 */
const validateScheduledAt = (scheduledAt, { allowPast = false } = {}) => {
  if (!scheduledAt) {
    throw new AppError('La fecha y hora de la cita son requeridas', 'INVALID_SCHEDULED_AT');
  }
  let dateValue;
  if (scheduledAt instanceof Date) {
    dateValue = scheduledAt;
  } else if (scheduledAt instanceof Timestamp) {
    dateValue = scheduledAt.toDate();
  } else if (typeof scheduledAt?.toDate === 'function') {
    dateValue = scheduledAt.toDate();
  } else {
    throw new AppError('Formato de fecha inválido', 'INVALID_SCHEDULED_AT');
  }
  if (Number.isNaN(dateValue.getTime())) {
    throw new AppError('Formato de fecha inválido', 'INVALID_SCHEDULED_AT');
  }
  if (!allowPast && dateValue.getTime() < Date.now() - 60_000) {
    // tolerancia 1 min para clock skew
    throw new AppError('La cita no puede estar en el pasado', 'INVALID_SCHEDULED_AT');
  }
};

const validateReminderMinutes = (reminderMinutes) => {
  if (!REMINDER_MINUTES_OPTIONS.includes(reminderMinutes)) {
    throw new AppError(
      'El recordatorio debe ser una de las opciones disponibles',
      'INVALID_REMINDER'
    );
  }
};

/**
 * Valida petIds: array no vacío, dentro del cap, todas las pets existen y
 * pertenecen al mismo hogar (cross-tenant defense).
 *
 * Devuelve un Map<petId, petName> para que el caller pueda hacer snapshot
 * de nombres en el log payload.
 */
const validatePetIdsAndResolveNames = async (petIds, familyId) => {
  if (!Array.isArray(petIds)) {
    throw new AppError('petIds debe ser un array', 'INVALID_PET_IDS');
  }
  if (petIds.length === 0) {
    throw new AppError('Elegí al menos una mascota', 'INVALID_PET_IDS');
  }
  if (petIds.length > VALIDATION.MAX_PETS_PER_APPOINTMENT) {
    throw new AppError(
      `Máximo ${VALIDATION.MAX_PETS_PER_APPOINTMENT} mascotas por cita`,
      'INVALID_PET_IDS'
    );
  }
  // Dedupe defensivo
  const unique = [...new Set(petIds)];
  if (unique.length !== petIds.length) {
    throw new AppError('Hay mascotas duplicadas', 'INVALID_PET_IDS');
  }

  const petNames = new Map();
  for (const petId of petIds) {
    if (typeof petId !== 'string' || !petId) {
      throw new AppError('petId inválido', 'INVALID_PET_IDS');
    }
    const snap = await getDoc(doc(db, COLLECTIONS.PETS, petId));
    if (!snap.exists()) {
      throw new AppError(`La mascota ${petId} no existe`, 'INVALID_PET_IDS');
    }
    const data = snap.data();
    if (data.familyId !== familyId) {
      // Cross-tenant: el caller está intentando referenciar una pet de otro hogar.
      throw new AppError(
        'Una de las mascotas no pertenece al hogar',
        'CROSS_TENANT_PET'
      );
    }
    petNames.set(petId, data.name || 'mascota sin nombre');
  }
  return petNames;
};

// =====================================================================
// CRUD
// =====================================================================

/**
 * Crea una cita agendada para 1..N mascotas del hogar.
 *
 * @param {object} input
 * @param {string} input.uid             — quien crea la cita
 * @param {string} input.familyId
 * @param {string[]} input.petIds        — 1..10 pets, todas del mismo hogar
 * @param {string} input.type            — 'vet' | 'grooming' | ... (APPOINTMENT_TYPES)
 * @param {string} input.title
 * @param {string|null} input.notes
 * @param {Date} input.scheduledAt
 * @param {number} input.reminderMinutes — 0 | 15 | 30 | 60 | 120 | 1440
 * @returns {{ appointmentId: string }}
 */
export const createAppointment = async ({
  uid,
  familyId,
  petIds,
  type,
  title,
  notes = null,
  scheduledAt,
  reminderMinutes,
}) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');

  validateTitle(title);
  validateNotes(notes);
  validateType(type);
  validateScheduledAt(scheduledAt);
  validateReminderMinutes(reminderMinutes);
  const petNames = await validatePetIdsAndResolveNames(petIds, familyId);

  // Normalizar scheduledAt a Timestamp para Firestore.
  const scheduledAtTs =
    scheduledAt instanceof Timestamp
      ? scheduledAt
      : Timestamp.fromDate(scheduledAt instanceof Date ? scheduledAt : new Date(scheduledAt));

  try {
    const appointmentId = await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(collection(db, COLLECTIONS.APPOINTMENTS));

      transaction.set(appointmentRef, {
        familyId,
        petIds: [...petIds],
        type,
        title: title.trim(),
        notes: notes ? notes.trim() : null,
        scheduledAt: scheduledAtTs,
        reminderMinutes,
        reminderSentAt: null,
        completedAt: null,
        completedBy: null,
        cancelledAt: null,
        cancelledBy: null,
        deletedAt: null,
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId,
        feedingGroupId: null,
        petId: null,
        userId: uid,
        type: LOG_TYPES.APPOINTMENT_CREATED,
        timestamp: serverTimestamp(),
        payload: {
          appointmentId: appointmentRef.id,
          appointmentType: type,
          title: title.trim(),
          scheduledAt: scheduledAtTs,
          reminderMinutes,
          petIds: [...petIds],
          petNames: petIds.map((id) => petNames.get(id) || 'mascota'),
        },
      });

      return appointmentRef.id;
    });
    return { appointmentId };
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo crear la cita', err);
  }
};

/**
 * Actualiza campos editables de una cita pendiente.
 *
 * Campos editables: `type`, `title`, `notes`, `scheduledAt`, `reminderMinutes`.
 * NO se edita `petIds` (decisión explícita: si cambian las pets, crear
 * cita nueva).
 *
 * Si cambia `scheduledAt` o `reminderMinutes`, se resetea `reminderSentAt = null`
 * para que el worker re-evalúe si corresponde push con el horario nuevo.
 *
 * Rechaza updates en citas ya completadas/canceladas/borradas.
 */
export const updateAppointment = async ({ uid, appointmentId, updates }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!appointmentId) throw new AppError('Cita requerida', 'INVALID_INPUT');

  const ALLOWED = ['type', 'title', 'notes', 'scheduledAt', 'reminderMinutes'];
  const filtered = {};
  for (const key of Object.keys(updates || {})) {
    if (ALLOWED.includes(key)) {
      filtered[key] = updates[key];
    } else {
      throw new AppError(`Campo no editable: ${key}`, 'INVALID_FIELD');
    }
  }
  if (Object.keys(filtered).length === 0) {
    throw new AppError('No hay cambios para aplicar', 'NO_CHANGES');
  }

  if ('title' in filtered) validateTitle(filtered.title);
  if ('notes' in filtered) validateNotes(filtered.notes);
  if ('type' in filtered) validateType(filtered.type);
  if ('reminderMinutes' in filtered) validateReminderMinutes(filtered.reminderMinutes);
  if ('scheduledAt' in filtered) {
    // Permitimos pasado en update (caso: corregir cita atrasada antes de
    // marcarla completada). El service de complete/cancel maneja transiciones.
    validateScheduledAt(filtered.scheduledAt, { allowPast: true });
  }

  try {
    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      const snap = await transaction.get(appointmentRef);
      if (!snap.exists()) {
        throw new AppError('La cita no existe', 'APPOINTMENT_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) throw new AppError('La cita fue eliminada', 'APPOINTMENT_DELETED');
      if (current.completedAt) throw new AppError('La cita ya está completada', 'ALREADY_COMPLETED');
      if (current.cancelledAt) throw new AppError('La cita ya está cancelada', 'ALREADY_CANCELLED');

      const patch = { ...filtered, updatedAt: serverTimestamp() };

      if ('title' in patch) patch.title = patch.title.trim();
      if ('notes' in patch) patch.notes = patch.notes ? patch.notes.trim() : null;

      if ('scheduledAt' in patch) {
        const sa = patch.scheduledAt;
        patch.scheduledAt =
          sa instanceof Timestamp
            ? sa
            : Timestamp.fromDate(sa instanceof Date ? sa : new Date(sa));
        // Reset del envío para que el worker re-evalúe.
        patch.reminderSentAt = null;
      }
      if ('reminderMinutes' in patch) {
        // Idem: cambia el cuándo se notifica.
        patch.reminderSentAt = null;
      }

      transaction.update(appointmentRef, patch);
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo actualizar la cita', err);
  }
};

/**
 * Marca una cita como hecha. Atómico: setea completedAt/By + log inmutable.
 */
export const completeAppointment = async ({ uid, appointmentId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!appointmentId) throw new AppError('Cita requerida', 'INVALID_INPUT');

  try {
    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      const snap = await transaction.get(appointmentRef);
      if (!snap.exists()) {
        throw new AppError('La cita no existe', 'APPOINTMENT_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) throw new AppError('La cita fue eliminada', 'APPOINTMENT_DELETED');
      if (current.completedAt) throw new AppError('La cita ya está completada', 'ALREADY_COMPLETED');
      if (current.cancelledAt) throw new AppError('La cita ya está cancelada', 'ALREADY_CANCELLED');

      transaction.update(appointmentRef, {
        completedAt: serverTimestamp(),
        completedBy: uid,
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: current.familyId,
        feedingGroupId: null,
        petId: null,
        userId: uid,
        type: LOG_TYPES.APPOINTMENT_COMPLETED,
        timestamp: serverTimestamp(),
        payload: {
          appointmentId,
          appointmentType: current.type,
          title: current.title,
          scheduledAt: current.scheduledAt,
          petIds: current.petIds || [],
          // Snapshot existente en el doc; si lo hubiéramos guardado en el create
          // payload no hace falta resolverlo de nuevo. Lo dejamos al render
          // resolver via petMap.
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo marcar la cita como hecha', err);
  }
};

/**
 * Cancela una cita. Atómico: setea cancelledAt/By + log inmutable.
 */
export const cancelAppointment = async ({ uid, appointmentId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!appointmentId) throw new AppError('Cita requerida', 'INVALID_INPUT');

  try {
    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      const snap = await transaction.get(appointmentRef);
      if (!snap.exists()) {
        throw new AppError('La cita no existe', 'APPOINTMENT_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) throw new AppError('La cita fue eliminada', 'APPOINTMENT_DELETED');
      if (current.completedAt) throw new AppError('La cita ya está completada', 'ALREADY_COMPLETED');
      if (current.cancelledAt) throw new AppError('La cita ya está cancelada', 'ALREADY_CANCELLED');

      transaction.update(appointmentRef, {
        cancelledAt: serverTimestamp(),
        cancelledBy: uid,
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: current.familyId,
        feedingGroupId: null,
        petId: null,
        userId: uid,
        type: LOG_TYPES.APPOINTMENT_CANCELLED,
        timestamp: serverTimestamp(),
        payload: {
          appointmentId,
          appointmentType: current.type,
          title: current.title,
          scheduledAt: current.scheduledAt,
          petIds: current.petIds || [],
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo cancelar la cita', err);
  }
};

/**
 * Soft delete. Sin log (consistente con feedingGroups soft-delete).
 * La cita desaparece del listado pero los logs de creación/completed/
 * cancelled siguen visibles en la bitácora.
 */
export const softDeleteAppointment = async ({ uid, appointmentId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!appointmentId) throw new AppError('Cita requerida', 'INVALID_INPUT');

  try {
    await runTransaction(db, async (transaction) => {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      const snap = await transaction.get(appointmentRef);
      if (!snap.exists()) {
        throw new AppError('La cita no existe', 'APPOINTMENT_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) throw new AppError('La cita ya fue eliminada', 'APPOINTMENT_DELETED');

      transaction.update(appointmentRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo eliminar la cita', err);
  }
};

// =====================================================================
// READS
// =====================================================================

export const fetchAppointment = async (appointmentId) => {
  if (!appointmentId) throw new AppError('Cita requerida', 'INVALID_INPUT');
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.APPOINTMENTS, appointmentId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new FirestoreError('No se pudo cargar la cita', err);
  }
};

/**
 * Suscripción reactiva a citas del hogar (activas, no soft-deleted).
 * El caller sortea/filtra como necesite. La query filtra soft-deleted
 * client-side (consistente con feedingGroups).
 */
export const subscribeToAppointments = (familyId, onNext, onError) => {
  if (!familyId) {
    onError?.(new AppError('familyId requerido', 'INVALID_INPUT'));
    return () => {};
  }
  const appointmentsRef = collection(db, COLLECTIONS.APPOINTMENTS);
  const q = query(appointmentsRef, where('familyId', '==', familyId));

  return onSnapshot(
    q,
    (snapshot) => {
      const appointments = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((a) => !a.deletedAt);
      onNext(appointments);
    },
    (err) => {
      onError?.(new FirestoreError('No se pudieron cargar las citas', err));
    }
  );
};
